import { useRouter } from "expo-router";
import { Platform } from "react-native";

import { useAuth } from "~/hooks/useAuth";
import { usePurchases } from "~/hooks/usePurchases";
import { AppText, Button, ButtonText, Card, Stack } from "~/components/ui";
import { notify } from "~/utils/notify";
import { PRO_PRICE_TEXT } from "~/utils/plan-limits";

function packageLabel(pkg: {
  billingPeriod: "monthly" | "annual" | "other";
  priceText: string;
  title: string;
}) {
  if (pkg.billingPeriod === "monthly") {
    return `TaskScribe Pro monthly — ${pkg.priceText}`;
  }
  if (pkg.billingPeriod === "annual") {
    return `TaskScribe Pro annual — ${pkg.priceText}`;
  }
  return `${pkg.title} — ${pkg.priceText}`;
}

export function UpgradePrompt({
  reason = `Unlimited lists and tasks, incomplete-task alerts, and completion analytics for ${PRO_PRICE_TEXT}/month.`,
  title = "Upgrade to TaskScribe Pro",
}: {
  reason?: string;
  title?: string;
}) {
  const { user, refresh } = useAuth();
  const router = useRouter();
  const purchases = usePurchases(user?.id ?? null);
  const useNativePaywall =
    purchases.supportsNativePaywall && Platform.OS !== "web";

  const openPaywall = async () => {
    try {
      const result = await purchases.presentPaywall();
      if (result === "purchased" || result === "restored") {
        await refresh();
        notify("Welcome to Pro", "Your subscription is activating.");
      }
    } catch (error) {
      notify(
        "Purchase failed",
        error instanceof Error ? error.message : "Please try again.",
      );
    }
  };

  // A subscription is attached to an account, so a guest creates one first.
  if (!user) {
    return (
      <Card gap="sm">
        <AppText weight="semibold">{title}</AppText>
        <AppText size="xs">{reason}</AppText>
        <AppText size="xs">
          Create a free account first — it also backs up and syncs your data.
        </AppText>
        <Button onPress={() => router.push("/register" as never)}>
          <ButtonText>Create free account</ButtonText>
        </Button>
      </Card>
    );
  }

  return (
    <Card gap="sm">
      <AppText weight="semibold">{title}</AppText>
      <AppText size="xs">{reason}</AppText>
      {purchases.error ? (
        <AppText tone="danger" size="xs">
          {purchases.error}
        </AppText>
      ) : null}
      {purchases.configured && purchases.packages.length === 0 ? (
        <AppText tone="danger" size="xs">
          RevenueCat returned no packages. Check the TaskScribe offering and
          product identifiers before purchasing.
        </AppText>
      ) : null}
      <Stack gap="sm">
        {useNativePaywall ? (
          <Button disabled={purchases.purchasing} onPress={openPaywall}>
            <ButtonText>
              {purchases.purchasing ? "Opening..." : "See Pro plans"}
            </ButtonText>
          </Button>
        ) : (
          purchases.packages.map((pkg) => (
            <Button
              key={pkg.identifier}
              disabled={purchases.purchasing}
              onPress={async () => {
                try {
                  await purchases.purchase(pkg);
                  await refresh();
                  notify("Welcome to Pro", "Your subscription is activating.");
                } catch (error) {
                  if (
                    error &&
                    typeof error === "object" &&
                    "userCancelled" in error &&
                    (error as { userCancelled?: boolean }).userCancelled
                  ) {
                    return;
                  }
                  notify(
                    "Purchase failed",
                    error instanceof Error
                      ? error.message
                      : "Please try again.",
                  );
                }
              }}
            >
              <ButtonText>{packageLabel(pkg)}</ButtonText>
            </Button>
          ))
        )}
        <Button
          variant="outline"
          disabled={!purchases.configured || purchases.loading}
          onPress={async () => {
            try {
              await purchases.restore();
              await refresh();
              notify("Purchases restored");
            } catch (error) {
              notify(
                "Restore failed",
                error instanceof Error ? error.message : "Please try again.",
              );
            }
          }}
        >
          <ButtonText>
            {purchases.loading ? "Loading plans..." : "Restore purchases"}
          </ButtonText>
        </Button>
      </Stack>
    </Card>
  );
}
